import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MapPin, ShieldCheck, Truck } from "lucide-react";

const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "Germany", "France",
  "Netherlands", "Spain", "Italy", "Ireland", "Mexico", "Brazil", "Japan",
  "Singapore", "India", "Other",
];

const EMPTY = {
  full_name: "", line1: "", line2: "", city: "", state: "",
  postal_code: "", country: "United States", phone: "",
};

export default function Shipping() {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!user || typeof user !== "object") {
      setLoading(false);
      return;
    }
    if (initialized.current) return; // only load the server copy once
    initialized.current = true;
    api
      .get("/me/shipping")
      .then(({ data }) => {
        if (data.shipping_address) {
          const a = data.shipping_address;
          setForm({ ...EMPTY, ...a });
          setSavedAt(a.updated_at);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/me/shipping", form);
      setSavedAt(data.shipping_address.updated_at);
      toast.success("Shipping address saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!user || typeof user !== "object") {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-zinc-400">
        Please log in to manage your shipping address.
      </div>
    );
  }

  return (
    <div className="grid-bg min-h-[calc(100vh-73px)]">
      <div className="max-w-5xl mx-auto px-6 py-16">
        <div className="flex items-start gap-3 mb-3">
          <MapPin className="text-[#FBBF24]" size={28} />
          <div>
            <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter">
              Shipping Address
            </h1>
            <p className="text-zinc-500 mt-2 max-w-2xl">
              Where should we send your winnings? Save it once — we'll use it automatically
              every time you win an auction.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8 mt-8">
          <TrustTile icon={<ShieldCheck size={16} />} title="Private" sub="Only used for fulfilment" />
          <TrustTile icon={<Truck size={16} />} title="Fast delivery" sub="Priority mail on all wins" />
          <TrustTile icon={<MapPin size={16} />} title="Editable anytime" sub="Update before an auction closes" />
        </div>

        <form onSubmit={submit} className="card-surface p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Full name" testId="shipping-full-name" value={form.full_name} onChange={set("full_name")} required />
          <Field label="Phone (for carrier)" testId="shipping-phone" value={form.phone} onChange={set("phone")} />
          <Field label="Address line 1" testId="shipping-line1" value={form.line1} onChange={set("line1")} required className="md:col-span-2" placeholder="Street address, P.O. box, company" />
          <Field label="Address line 2" testId="shipping-line2" value={form.line2} onChange={set("line2")} className="md:col-span-2" placeholder="Apartment, suite, unit (optional)" />
          <Field label="City" testId="shipping-city" value={form.city} onChange={set("city")} required />
          <Field label="State / Province" testId="shipping-state" value={form.state} onChange={set("state")} />
          <Field label="Postal / ZIP" testId="shipping-postal" value={form.postal_code} onChange={set("postal_code")} required />
          <div>
            <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
              Country
            </label>
            <select
              required
              className="input-field"
              value={form.country}
              onChange={set("country")}
              data-testid="shipping-country"
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2 flex items-center justify-between pt-4 border-t border-white/5 mt-2">
            <div className="text-xs text-zinc-500">
              {savedAt
                ? `Last updated ${new Date(savedAt).toLocaleString()}`
                : "No address on file yet"}
            </div>
            <button
              type="submit"
              disabled={saving || loading}
              data-testid="shipping-save-button"
              className="btn-bid text-sm"
            >
              {saving ? "Saving…" : "Save address"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, required, placeholder, className = "", testId }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-widest text-zinc-500 mb-2 block">
        {label}
        {required && <span className="text-[#FBBF24]"> *</span>}
      </label>
      <input
        type="text"
        required={required}
        className="input-field"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testId}
      />
    </div>
  );
}

function TrustTile({ icon, title, sub }) {
  return (
    <div className="card-surface p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#1A1A24] border border-white/10 flex items-center justify-center text-[#22C55E]">
        {icon}
      </div>
      <div>
        <div className="font-semibold text-sm">{title}</div>
        <div className="text-[11px] text-zinc-500">{sub}</div>
      </div>
    </div>
  );
}
